import { useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CenteredSpinner } from '@/components/CenteredSpinner';
import StudyDashboard from '../simops/-components/dashboard/StudyDashboard';
import { isEvalToolStudy } from '../simops/-data/constants';
import {
  flattenStudyPages,
  studyListInfiniteOptions,
  studyListLoader,
} from '../simops/-data/study-list';

/**
 * EvalTool front door (its own activity-bar app): the same study library
 * `/simops` shows — one table, one set of columns — opened on the Completed
 * tab, because finished work is what this product is for.
 *
 * Rows open under `/evaltool/$studyId/...`, so a link a reviewer sends reads
 * as the product they were in. Unfinished studies are still reachable from the
 * other tabs; those rows go to `/simops/$studyId`, which is where the meshing
 * and simulate steps live.
 */
function EvalToolLibraryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const studiesQuery = useInfiniteQuery(studyListInfiniteOptions());
  const studies = useMemo(
    () => flattenStudyPages(studiesQuery.data),
    [studiesQuery.data],
  );

  const handleOpenStudy = (studyId: string) => {
    // A study EvalTool has a surface for opens on `/results` — the workbench
    // for a finished one, `ReviewView` for one waiting on a decision. Anything
    // else belongs to SimOps. Same predicate the `/evaltool/$studyId` loader
    // forwards on, so a row and a hand-typed URL cannot disagree.
    const study = studies.find((s) => s.id === studyId);
    void navigate(
      study && isEvalToolStudy(study.status)
        ? { to: '/evaltool/$studyId/results', params: { studyId } }
        : { to: '/simops/$studyId', params: { studyId } },
    );
  };

  if (studiesQuery.isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <div className="h-full flex-1 overflow-hidden">
      <StudyDashboard
        studies={studies}
        library="evaltool"
        error={studiesQuery.isError ? t('evaltoolLibrary.loadError') : null}
        onOpenStudy={handleOpenStudy}
        hasNextPage={studiesQuery.hasNextPage}
        isFetchingNextPage={studiesQuery.isFetchingNextPage}
        onLoadMore={() => void studiesQuery.fetchNextPage()}
      />
    </div>
  );
}

export const Route = createFileRoute('/_app/evaltool/')({
  // The same warm `/simops` does, over the same cache entries — see
  // `studyListLoader`.
  loader: ({ context: { queryClient } }) => studyListLoader(queryClient),
  component: EvalToolLibraryPage,
});
